import React, { useState, useEffect } from 'react';

// Chỉ import các phần tử nhỏ cần thiết để tối ưu Tree-shaking cho Bundle
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ContainerGrid from "./ContainerGrid";//Sử dụng Grid này thay cho Grid của MUI để nó response theo container thay vì màn hình thiết bị
import {loadBabelOnce} from "../utils";

// Import sẵn Google Chart để bơm vào cho User dùng nếu họ thích vẽ đồ thị riêng
import { Chart } from "react-google-charts";
import { bienDichComponent } from './bienDichJsx';//Biên dịch script JSX -> component (an toàn với hàm phụ Babel chèn đầu mã)

// =====================================================================
// COMPONENT: TRÌNH BIÊN DỊCH GÓC NHÌN PIVOT TÙY CHỈNH (RUNTIME COMPILER)
// =====================================================================
const DynamicCustomPivot = ({ 
    script,         // Đoạn code React/JSX do người dùng cấu hình
    pivotData,      // Dữ liệu ma trận đã được tính toán sẵn (rowKeys, colKeys, matrix...)
    rawReportData,  // Dữ liệu báo cáo gốc dạng mảng ban đầu (Nếu user muốn tự map/reduce lại)
    columns,        // Cấu hình các cột (label, type...)
    configMeta,     // Cấu hình Pivot user đang chọn (rowFields, valFields, aggType...)
    context         // App context (Token, APIs...)
}) => {
  // Bổ sung MUI vào State để chứa module sau khi import động thành công
  const [CompilerConfig, setCompilerConfig] = useState({ Component: null, MUI: null, error: null, isLoading: true });

  useEffect(() => {
    let isMounted = true;
    
    const compileScript = async () => {
      try {
        setCompilerConfig(prev => ({ ...prev, isLoading: true, error: null }));

        // 1. TẢI ĐỘNG (LAZY-LOAD) BABEL VÀ TOÀN BỘ THƯ VIỆN MUI CÙNG LÚC
        const loadBabel = loadBabelOnce();
        const [Babel, DynamicallyLoadedMUI] = await Promise.all([
          loadBabel,
          import('@mui/material') // Tải động toàn bộ MUI (Sẽ tách thành chunk riêng)
        ]);

        // 2. PHÂN TÍCH SCRIPT CỦA USER VÀ KIỂM TRA TÍNH TƯƠNG THÍCH
        let scriptBody = script;
        if (!scriptBody.includes("return ")) {
            scriptBody = "return (\n<React.Fragment>\n" + scriptBody + "\n</React.Fragment>\n);";
        }

        // 3. BỌC SCRIPT THÀNH REACT FUNCTIONAL COMPONENT, BƠM ĐỦ ĐỒ NGHỀ CHO USER
        const wrappedScript = 
          "(function UserCustomPivotView(props) {\n" +
          "  const { MUI, React, Chart, pivotData, rawReportData, columns, configMeta, context } = props;\n" +
          "  " + scriptBody + "\n" +
          "})";

        // 5. DỊCH JSX + KHỞI TẠO COMPONENT (xem bienDichJsx: không ghép `return ${ma}`)
        const UserComponent = bienDichComponent(Babel, wrappedScript, ['React', 'MUI', 'ContainerGrid', 'Chart'], [React, DynamicallyLoadedMUI, ContainerGrid, Chart]);

        if (isMounted) {
            setCompilerConfig({ 
                Component: UserComponent, 
                MUI: DynamicallyLoadedMUI, 
                ContainerGrid,
                error: null, 
                isLoading: false 
            });
        }
      } catch (err) {
        if (isMounted) setCompilerConfig({ Component: null, MUI: null, error: err.message, isLoading: false });
      }
    };

    if (script) {
        compileScript();
    } else {
        if (isMounted) setCompilerConfig({ Component: null, MUI: null, error: "Không có mã script hiển thị.", isLoading: false });
    }
    
    return () => { isMounted = false; };
  }, [script, pivotData]); // Re-compile nếu script hoặc data thay đổi lớn (Tùy chọn)

  if (CompilerConfig.isLoading) return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 3, justifyContent: 'center', flexGrow: 1 }}>
      <CircularProgress size={24} />
      <Typography color="text.secondary">Đang biên dịch góc nhìn tuỳ chỉnh...</Typography>
    </Box>
  );

  if (CompilerConfig.error) return (
    <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
      <strong>Lỗi biên dịch Code Giao diện:</strong> {CompilerConfig.error}
    </Alert>
  );

  const { Component, MUI } = CompilerConfig;
  if (!Component || !MUI) return null;

  // 6. RENDER COMPONENT VÀ TRUYỀN PROPS DỮ LIỆU
  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, bgcolor: 'background.default' }}>
        <Component 
          pivotData={pivotData} 
          rawReportData={rawReportData} 
          columns={columns} 
          configMeta={configMeta} 
          context={context} 
          MUI={MUI} 
          ContainerGrid={ContainerGrid}
          React={React} 
          Chart={Chart}
        />
    </Box>
  );
};

export default DynamicCustomPivot;